import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase.js';
import { toast } from 'react-toastify';

const STATUS_LABELS = {
  open: 'Open',
  resolved: 'Resolved',
};

function formatViolationDate(value) {
  if (!value) return '—';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

async function fetchUserLotIds(user) {
  if (!user) return [];
  const ownersQuery = query(collection(db, 'owners'), where('OwnerId', '==', user.uid));
  const ownersQuery2 = query(collection(db, 'owners'), where('OwnerId2', '==', user.uid));
  const [snapshot1, snapshot2] = await Promise.all([getDocs(ownersQuery), getDocs(ownersQuery2)]);
  const lots = [...snapshot1.docs, ...snapshot2.docs].map((row) => row.id);
  return [...new Set(lots)];
}

function ViolationsPage({ user, isAdmin, isMobile }) {
  const [violations, setViolations] = useState([]);
  const [userLotIds, setUserLotIds] = useState([]);
  const [ownerDirectory, setOwnerDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newViolation, setNewViolation] = useState({ lotId: '', title: '', description: '' });
  const [statusFilter, setStatusFilter] = useState('all');

  const lotLabelById = useMemo(() => {
    const map = new Map();
    ownerDirectory.forEach((lot) => {
      map.set(lot.lotId, lot.tract ? `Lot ${lot.tract}` : lot.lotId);
    });
    return map;
  }, [ownerDirectory]);

  const loadOwnerDirectory = useCallback(async () => {
    const snapshot = await getDocs(collection(db, 'owners'));
    const lots = snapshot.docs.map((row) => ({
      lotId: row.id,
      tract: row.data().tract || row.id,
      address: row.data().address || '',
    }));
    lots.sort((a, b) => String(a.tract).localeCompare(String(b.tract), undefined, { numeric: true }));
    setOwnerDirectory(lots);
    return lots;
  }, []);

  const loadViolations = useCallback(async () => {
    setLoading(true);
    try {
      const lots = isAdmin ? [] : await fetchUserLotIds(user);
      setUserLotIds(lots);

      let rows = [];
      if (isAdmin) {
        const snapshot = await getDocs(query(collection(db, 'violations'), orderBy('createdAt', 'desc')));
        rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
      } else if (lots.length === 0) {
        rows = [];
      } else if (lots.length <= 10) {
        const snapshot = await getDocs(query(collection(db, 'violations'), where('lotId', 'in', lots)));
        rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
      } else {
        const chunks = [];
        for (let i = 0; i < lots.length; i += 10) {
          chunks.push(lots.slice(i, i + 10));
        }
        const snapshots = await Promise.all(
          chunks.map((chunk) => getDocs(query(collection(db, 'violations'), where('lotId', 'in', chunk)))),
        );
        const merged = new Map();
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((row) => merged.set(row.id, { id: row.id, ...row.data() }));
        });
        rows = [...merged.values()].sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
      }

      setViolations(rows);
    } catch (error) {
      console.error('Failed to load violations:', error);
      toast.error(error.message || 'Failed to load violations');
      setViolations([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user]);

  useEffect(() => {
    document.title = 'Violations | Nantahala Mountain Resort';
    return () => {
      document.title = 'NMR Community';
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      loadOwnerDirectory().catch((error) => console.error(error));
    }
    loadViolations();
  }, [user, isAdmin, loadOwnerDirectory, loadViolations]);

  const filteredViolations = useMemo(() => {
    if (statusFilter === 'all') return violations;
    return violations.filter((row) => row.status === statusFilter);
  }, [violations, statusFilter]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!isAdmin) return;

    const lotId = newViolation.lotId.trim();
    const title = newViolation.title.trim();
    const description = newViolation.description.trim();

    if (!lotId || !title || !description) {
      toast.error('Lot, title, and description are required.');
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, 'violations'), {
        lotId,
        title,
        description,
        status: 'open',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
      });
      toast.success('Violation recorded.');
      setNewViolation({ lotId: '', title: '', description: '' });
      setShowCreateForm(false);
      await loadViolations();
    } catch (error) {
      console.error('Create violation failed:', error);
      toast.error(error.message || 'Could not create violation');
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (violationId) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'violations', violationId), {
        status: 'resolved',
        resolvedAt: serverTimestamp(),
        resolvedBy: user.uid,
      });
      toast.success('Violation marked resolved.');
      await loadViolations();
    } catch (error) {
      console.error('Resolve violation failed:', error);
      toast.error(error.message || 'Could not resolve violation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`docs-page ${isMobile ? 'docs-page-mobile' : ''}`}>
      <article className="docs-card docs-card-wide">
        <header className="docs-header">
          <h1 className="docs-title">HOA Violations</h1>
          <p className="docs-meta">
            {isAdmin
              ? 'Review, record, and resolve lot violations across the community.'
              : 'View compliance notices tied to your lot(s).'}
          </p>
        </header>

        <section className="docs-section">
          <div className="violations-toolbar">
            <label className="violations-filter">
              Status
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </label>

            {isAdmin ? (
              <button
                type="button"
                className="primary-button"
                onClick={() => setShowCreateForm((open) => !open)}
              >
                {showCreateForm ? 'Cancel' : 'Record violation'}
              </button>
            ) : null}
          </div>

          {!isAdmin && userLotIds.length === 0 && !loading ? (
            <p className="violations-empty">
              No lots are linked to your account. Contact the board if you believe this is an error.
            </p>
          ) : null}

          {isAdmin && showCreateForm ? (
            <form className="violations-create-form" onSubmit={handleCreate}>
              <h2>Record new violation</h2>
              <label>
                Lot
                <select
                  value={newViolation.lotId}
                  onChange={(e) => setNewViolation((prev) => ({ ...prev, lotId: e.target.value }))}
                  required
                >
                  <option value="">Select lot…</option>
                  {ownerDirectory.map((lot) => (
                    <option key={lot.lotId} value={lot.lotId}>
                      {lot.tract}
                      {lot.address ? ` — ${lot.address}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title
                <input
                  type="text"
                  value={newViolation.title}
                  onChange={(e) => setNewViolation((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Unapproved exterior modification"
                  required
                />
              </label>
              <label>
                Description
                <textarea
                  value={newViolation.description}
                  onChange={(e) => setNewViolation((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Details of the violation and any required corrective action"
                  rows={4}
                  required
                />
              </label>
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Saving…' : 'Create violation'}
              </button>
            </form>
          ) : null}

          {loading ? <p>Loading violations…</p> : null}

          {!loading && filteredViolations.length === 0 ? (
            <p className="violations-empty">No violations match this filter.</p>
          ) : null}

          <ul className="violations-list">
            {filteredViolations.map((violation) => (
              <li key={violation.id} className={`violations-item violations-item--${violation.status || 'open'}`}>
                <div className="violations-item-header">
                  <div>
                    <h3>{violation.title}</h3>
                    <p className="violations-item-meta">
                      {lotLabelById.get(violation.lotId) || violation.lotId}
                      {' · '}
                      {STATUS_LABELS[violation.status] || violation.status || 'Open'}
                      {' · '}
                      Opened {formatViolationDate(violation.createdAt)}
                    </p>
                  </div>
                  <span className={`violations-status-badge violations-status-badge--${violation.status || 'open'}`}>
                    {STATUS_LABELS[violation.status] || violation.status || 'Open'}
                  </span>
                </div>
                <p className="violations-item-description">{violation.description}</p>
                {violation.status === 'resolved' ? (
                  <p className="violations-item-meta">
                    Resolved {formatViolationDate(violation.resolvedAt)}
                  </p>
                ) : null}
                {isAdmin && violation.status !== 'resolved' ? (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={saving}
                    onClick={() => handleResolve(violation.id)}
                  >
                    Mark resolved
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      </article>
    </div>
  );
}

export default ViolationsPage;